/**
 * User-state WS sync — three granular subscriptions keep the account /
 * openOrders / spotBalances caches fresh in real time:
 *
 *   - `allDexsClearinghouseState({ user })` → account cache. One event carries
 *     `[dex, state][]` tuples for EVERY dex ("" = main) every ~4–5s, so a
 *     single push atomically merges main + xyz (and future HIP-3 dexes need
 *     no new subscription). Also refreshes the `accountXyz` cache + module
 *     fallback that the REST backstop merges from.
 *   - `openOrders({ user })`  → openOrders cache (main portion; xyz portion
 *     is owned by `useHyperXyzSync`)
 *   - `spotState({ user })`   → spotBalances cache
 *
 * History: this replaced a single `webData2` subscription. Hyperliquid removed
 * that WS topic server-side (discovered 2026-07: subscribe is rejected with
 * "Error parsing JSON into valid websocket request", and with no onError
 * listener the failure was silent — all real-time account data froze, leaving
 * only the 5–10min REST backstops). The SDK deprecates `webData2` in favor of
 * "webData3 and other component subscriptions"; `webData3` is a slim summary
 * stream, so we use the component topics. Market meta/assetCtxs (the remaining
 * webData2 payload member) is real-time via `useTradeMetaSync` (`assetCtxs`
 * subs) with the 60s REST poll in `useTradeMetaQuery` as universe bootstrap +
 * backstop.
 *
 * Why WS-first at all: the Hyperliquid REST API is IP-rate-limited
 * (~1200 req/min). On a shared office IP, per-user polling would saturate
 * that budget quickly; WS push keeps the REST pollers as long-interval
 * backstops.
 *
 * Coexists with `useHyperUserEvents` (`orderUpdates` / `userFills`): the two
 * streams are idempotent — whichever event arrives first updates the cache;
 * the other becomes a no-op.
 *
 * Mounted by `components/hyperliquid/hyper-user-events-sync.tsx`; no-ops via
 * `enabled: false` while logged out (`useWallet()` returns a null address).
 */
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useHyperliquidSubscription } from "#/features/trade/hooks/use-hyperliquid-subscription";
import { XYZ_DEX_NAME } from "#/features/trade/lib/hyperliquid";
import { isWebSocketClosedError } from "#/features/trade/lib/hyperliquid-error";
import { queryKeys } from "./query-keys";
import {
	type AccountState,
	type AssetPosition,
	mergeAcrossDexes,
	type OpenOrder,
	type SpotState,
	setXyzAccountFallback,
} from "./use-hyperliquid-account";
import { useWallet } from "./use-wallet";

/**
 * Merge a fresh main-DEX orders push into the openOrders cache: keep
 * optimistic placeholders (negative oid) and the xyz-tagged portion (owned by
 * `useHyperXyzSync`), replace everything else. An xyz-tagged optimistic
 * placeholder belongs to the optimistic bucket only — excluding it from
 * `xyzOrders` prevents a duplicate row. Pure so it's unit-tested without
 * React.
 */
export function patchMainOpenOrders(
	old: OpenOrder[] | undefined,
	mainOrders: OpenOrder[],
): OpenOrder[] {
	const optimistic = (old ?? []).filter((o) => o.oid < 0);
	const xyzOrders = (old ?? []).filter((o) => o.__dex === "xyz" && o.oid >= 0);
	return [...optimistic, ...mainOrders, ...xyzOrders];
}

// Fires on the initial-subscribe rejection (via useHyperliquidSubscription's
// onError) AND — since SDK 0.33 — when a confirmed subscription dies
// (re-subscribe rejected after reconnect / permanent termination, via
// SubscriptionOptions.onError). A dead subscription here means NO real-time
// account data — the exact failure mode that went unnoticed when HL removed
// webData2 — so always surface it. Transient WS drops self-heal via
// auto-resubscribe and are filtered out.
// ponytail: observation-only — the SDK removes the subscription after the
// options.onError fires; add auto-resubscribe (re-run the effect) if these
// alerts ever show up for a topic that still exists.
function captureSubscribeFailure(topic: string) {
	return (error: unknown) => {
		if (isWebSocketClosedError(error)) return;
		const msg = `[main-sync] ws-${topic}-failed`;
		console.error(msg, error);
		Sentry.withScope((scope) => {
			scope.setTag("main_sync", topic);
			scope.setLevel("error");
			scope.setExtra(
				"error",
				error instanceof Error ? error.message : String(error),
			);
			Sentry.captureMessage(msg);
		});
	};
}

export function useHyperMainSync() {
	const { address } = useWallet();
	const queryClient = useQueryClient();

	useHyperliquidSubscription({
		enabled: address !== null,
		deps: [address],
		onError: captureSubscribeFailure("account"),
		subscribe: (client) =>
			client.allDexsClearinghouseState(
				{ user: address as `0x${string}` },
				(event) => {
					if (!address) return;
					const states = event.clearinghouseStates;
					const main = states.find(([dex]) => dex === "")?.[1] as
						| AccountState
						| undefined;
					if (!main) return;
					const xyz = states.find(([dex]) => dex === XYZ_DEX_NAME)?.[1] as
						| AccountState
						| undefined;

					const accountKey = queryKeys.hyper.account(address);
					if (!xyz) {
						// The xyz tuple's presence is a server observation, not a
						// contract — fall back to preserving previously-merged xyz
						// positions rather than dropping them.
						queryClient.setQueryData<AccountState>(accountKey, (old) => {
							if (!old) return main;
							const xyzPositions = old.assetPositions.filter(
								(p) => (p as AssetPosition).__dex === "xyz",
							);
							if (xyzPositions.length === 0) return main;
							return {
								...main,
								assetPositions: [...main.assetPositions, ...xyzPositions],
							};
						});
					} else {
						setXyzAccountFallback(address, xyz);
						queryClient.setQueryData(queryKeys.hyper.accountXyz(address), xyz);
						queryClient.setQueryData<AccountState>(
							accountKey,
							mergeAcrossDexes(main, xyz),
						);
					}
				},
				{ onError: captureSubscribeFailure("account") },
			),
	});

	useHyperliquidSubscription({
		enabled: address !== null,
		deps: [address],
		onError: captureSubscribeFailure("openOrders"),
		subscribe: (client) =>
			client.openOrders(
				{ user: address as `0x${string}` },
				(event) => {
					if (!address) return;
					queryClient.setQueryData<OpenOrder[]>(
						queryKeys.hyper.openOrders(address),
						(old) => patchMainOpenOrders(old, event.orders as OpenOrder[]),
					);
				},
				{ onError: captureSubscribeFailure("openOrders") },
			),
	});

	useHyperliquidSubscription({
		enabled: address !== null,
		deps: [address],
		onError: captureSubscribeFailure("spotState"),
		subscribe: (client) =>
			client.spotState(
				{ user: address as `0x${string}` },
				(event) => {
					if (!address) return;
					queryClient.setQueryData<SpotState>(
						queryKeys.hyper.spotBalances(address),
						event.spotState as SpotState,
					);
				},
				{ onError: captureSubscribeFailure("spotState") },
			),
	});
}
